<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Холдинги</div>

            <div class="content-header__actions">
                <router-link :to="{ name: 'AddHolding' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Добавить
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--1-line-filters"></div>
        </div>

        <FilterPanel
            @search="onSearch"
            @reset="onReset"
        >
            <UIInput
                id="name"
                v-model="holdingsFilter.name"
                label="Название"
            />

            <UIInput
                id="sap_code"
                v-model="holdingsFilter.sap_code"
                label="SAP-код"
            />

            <UISelect
                id="activity"
                v-model="holdingsStatusFilter"
                :options="statusOptions"
                label="Статус"
            />
        </FilterPanel>

        <UISearchResults
            :count="holdingsPaginationMeta?.pagination.total || 0"
            :is-visible-sort="false"
        />

        <UILoader v-if="isHoldingsLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arHoldings"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
                @row-click="openHoldingModal"
            >
                <template #actions="{ row }">
                    <UIDropDown as-modal-on-mobile>
                        <template #elements>
                            <router-link
                                :to="{ name: 'EditHolding', params: { holding_id: row.id } }"
                                class="ui-dropdown-menu__element"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                <span>Редактировать</span>
                            </router-link>

                            <li
                                v-if="row.status === HoldingStatus.Deactivated"
                                class="ui-dropdown-menu__element"
                                @click="handleActivateHolding(row as HoldingPaginatorItem)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/check.svg" />
                                <span>Активировать</span>
                            </li>

                            <li
                                v-if="row.status === HoldingStatus.Active"
                                class="ui-dropdown-menu__element danger"
                                @click="handleDeactivateHolding(row as HoldingPaginatorItem)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                                <span>Деактивировать</span>
                            </li>
                        </template>
                    </UIDropDown>
                </template>

                <template #status="{ row }">
                    {{ getHoldingStatusLabel(row.status) }}
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="holdingsPaginationMeta?.pagination.last_page ?? 1"
                :current-page="holdingsPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="holdingsPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, inject, onMounted, ref } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import {
        getHoldingStatusLabel,
        getHoldingStatusSelectOptions,
        HoldingStatus,
        HoldingPaginatorItem,
        HoldingsRequestFilter,
    } from '@/types/holding.type.ts';

    import useHoldingRepository from '@/repositories/holding/useHoldingRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import { useHoldingsFilterStore } from '@/stores/holdings/holdings-filter.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import HoldingModal from '@/modals/HoldingModal/HoldingModal.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import HoldingConfirmActivate from '@/components/holdings/HoldingConfirmActivate/HoldingConfirmActivate.vue';
    import HoldingConfirmDeactivate from '@/components/holdings/HoldingConfirmDeactivate/HoldingConfirmDeactivate.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        arHoldings,
        holdingsPaginationMeta,
        isHoldingsLoading,
        getHoldings,
        activateHolding,
        deactivateHolding,
    } = useHoldingRepository();
    const holdingsFilterStore = useHoldingsFilterStore();
    const { showSuccess } = useNotifications();

    const holdingsFilter = ref<HoldingsRequestFilter>({ ...holdingsFilterStore.$state });
    const statusOptions = ref(getHoldingStatusSelectOptions());
    const headers = ref([
        { label: 'SAP-код', key: 'sap_code', sortable: false },
        { label: 'Название холдинга', key: 'name', sortable: true },
        { label: 'Кол-во ДЦ', key: 'dealers_count', sortable: true },
        { label: 'Коды ДЦ', key: 'dealers_sap_codes', sortable: false },
        { label: 'Статус', key: 'status', sortable: false },
    ]);

    const holdingsStatusFilter = computed<HoldingStatus | undefined>({
        get: () => {
            if (holdingsFilter.value.status !== undefined && holdingsFilter.value.status.length > 0) {
                return holdingsFilter.value.status[0];
            }

            return undefined;
        },
        set: (status?: HoldingStatus) => {
            holdingsFilter.value.status = status !== undefined ? [status] : undefined;
        },
    });

    const sortKey = computed<string | undefined>(() => {
        if (holdingsFilter.value.sort) {
            return Object.keys(holdingsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (holdingsFilter.value.sort) {
            return Object.values(holdingsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    function onSearch(): void {
        holdingsFilter.value.page = 1;
        fetchHoldings();
    }

    function onReset(): void {
        holdingsFilter.value.name = '';
        holdingsFilter.value.sap_code = '';
        holdingsFilter.value.status = undefined;
        holdingsFilter.value.page = 1;

        fetchHoldings();
    }

    function changePage(page: number): void {
        holdingsFilter.value.page = page;

        fetchHoldings();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        holdingsFilter.value.sort = dir === undefined ? [{ name: 'asc' }] : [{ [key]: dir }];
        fetchHoldings();
    }

    function openHoldingModal(holding: HoldingPaginatorItem): void {
        $modal.open({
            component: HoldingModal,
            props: {
                holdingId: holding.id,
            },
        });
    }

    function handleActivateHolding(holding: HoldingPaginatorItem): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () => h(HoldingConfirmActivate, { holding }),
            },
            props: {
                onConfirm: async () => {
                    await activateHolding(holding.id);

                    showSuccess({
                        title: 'Холдинг активирован',
                    });

                    fetchHoldings();
                },
            },
        });
    }

    function handleDeactivateHolding(holding: HoldingPaginatorItem): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () => h(HoldingConfirmDeactivate, { holding }),
            },
            props: {
                onConfirm: async () => {
                    await deactivateHolding(holding.id);

                    showSuccess({
                        title: 'Холдинг деактивирован',
                    });

                    fetchHoldings();
                },
            },
        });
    }

    function mapHoldingsFilterToRequestFilter(): HoldingsRequestFilter {
        const requestFilter: HoldingsRequestFilter = {
            page: holdingsFilter.value.page,
            sort: holdingsFilter.value.sort,
        };

        if (holdingsFilter.value.name !== undefined && holdingsFilter.value.name.length > 0) {
            requestFilter.name = holdingsFilter.value.name;
        }

        if (holdingsFilter.value.sap_code !== undefined && holdingsFilter.value.sap_code.length > 0) {
            requestFilter.sap_code = holdingsFilter.value.sap_code;
        }

        if (holdingsFilter.value.status !== undefined && holdingsFilter.value.status.length > 0) {
            requestFilter.status = holdingsFilter.value.status;
        }

        return requestFilter;
    }

    function fetchHoldings(): void {
        holdingsFilterStore.$patch({ ...holdingsFilter.value });
        getHoldings(mapHoldingsFilterToRequestFilter());
    }

    onMounted(() => {
        fetchHoldings();
    });
</script>
